import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { CITIES, stateFromSlug, stateName, stateSlug, citiesIn, cityFromSlug, cityPath } from "@/lib/constants";
import { useSeo } from "@/lib/seo";
import { Crumbs, FaqSection, ChipLinks, QIResults, ListCta, faqJsonLd } from "@/components/site/LocalBlocks";
import { QICard } from "@/components/site/QICard";
import SEO from "@/lib/seoContent.json";

const fill = (s, city, code) => s.replaceAll("{city}", city).replaceAll("{name}", stateName(code)).replaceAll("{code}", code);
const faqs = (city, code) => SEO.cityFaqs.map(([q, a]) => [fill(q, city, code), fill(a, city, code)]);

const isLocal = (q, city, code) =>
  (q.metros_served || []).includes(`${city}, ${code}`) || (q.city || "").trim().toLowerCase() === city.toLowerCase();

export default function CityPage() {
  const { stateSlug: sSlug, citySlug: cSlug } = useParams();
  const code = stateFromSlug(sSlug);
  const city = code ? cityFromSlug(code, cSlug) : null;
  const name = code ? stateName(code) : "";
  const [qis, setQis] = useState(null);

  const jsonLd = useMemo(() => city && faqJsonLd(faqs(city, code)), [city, code]);
  useSeo({
    title: city ? fill(SEO.cityTitle, city, code) : "City not found",
    description: city ? fill(SEO.cityDescription, city, code) : "",
    jsonLd,
  });

  useEffect(() => {
    if (!city) {
      return;
    }
    setQis(null);
    api.get("/qis", { params: { state: code } }).then(({ data }) => setQis(data)).catch(() => setQis([]));
  }, [city, code]);

  if (!city) return <div className="py-32 text-center text-white/60" data-testid="city-not-found">City not found. <Link to="/1031-exchange" className="text-qi-pink">See all states</Link></div>;

  const local = qis && qis.filter((q) => isLocal(q, city, code));
  const others = qis && qis.filter((q) => !isLocal(q, city, code));
  const sameState = citiesIn(code).filter(([c]) => c !== city).map(([c]) => [c, cityPath(c, code)]);
  const otherMetros = CITIES.filter(([, c]) => c !== code).slice(0, 12).map(([c, s]) => [`${c}, ${s}`, cityPath(c, s)]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="city-page">
      <Crumbs items={[["Home", "/"], ["By state", "/1031-exchange"], [name, `/1031-exchange/${stateSlug(code)}`], [city]]} />
      <div className="relative mt-8">
        <div className="absolute -top-24 -right-10 w-80 h-80 bg-qi-pink/15 rounded-full blur-3xl pointer-events-none" />
        <span className="eyebrow inline-flex items-center gap-2"><MapPin size={13} />{city}, {code} · 1031 exchange</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-4 max-w-4xl leading-[1.04]" data-testid="city-title">
          Find a 1031 qualified intermediary in <span className="text-qi-pink">{city}</span>
        </h1>
        <p className="text-white/60 mt-5 max-w-2xl leading-relaxed">{fill(SEO.cityIntro, city, code)}</p>
      </div>
      <div className="mt-12"><QIResults qis={local} place={`${city}, ${code}`} /></div>
      {others?.length > 0 && (
        <section className="mt-14" data-testid="city-also-serving">
          <span className="eyebrow">Also serving {name}</span>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-5">{others.map((qi) => <QICard key={qi.profile_id} qi={qi} />)}</div>
        </section>
      )}
      <ListCta place={city} />
      <FaqSection heading={`1031 exchanges in ${city}: common questions`} items={faqs(city, code)} />
      {sameState.length > 0 && <ChipLinks title={`More cities in ${name}`} testid="city-same-state" links={sameState} all={[`All of ${name}`, `/1031-exchange/${stateSlug(code)}`]} />}
      <ChipLinks title="Other metros" testid="city-other" links={otherMetros} all={["All states", "/1031-exchange"]} />
    </div>
  );
}
